import { useApiQuery } from '@/hooks/useApiQuery';
import { type ActivityScope, activityKeys, getActivityCount } from '@/lib/activity-api';

/**
 * The gold count on the Activity sidebar row.
 *
 * One section, one badge, and it counts the shared action lane and nothing else —
 * a workspace with 400 recorded rows and nothing waiting shows no badge at all.
 * That is what makes zero reachable and worth reaching.
 *
 * Returns 0 rather than undefined while loading so the row never flashes a
 * badge that then disappears.
 */
export function useActivityBadge(scope: ActivityScope | null): number {
    const { data } = useApiQuery(
        scope ? activityKeys.count(scope) : ['activity', 'count', 'none'],
        () => getActivityCount(scope!),
        {
            enabled: !!scope,
            // The sidebar is mounted on every page; a slightly stale count is
            // fine and far cheaper than refetching on each navigation. The
            // endpoint is a single indexed count, so resolving can refetch it
            // eagerly without cost.
            staleTime: 60_000,
        },
    );
    return data?.open_total ?? 0;
}
